/*
  Keepin' It Halal Brand - color-first / size-locked variant picker + size-guide modal.
  Works regardless of Shopify option order (uses colorPos/sizePos from the product data).

  Accessibility notes (section 28):
    - Both option groups are ARIA radio groups and implement the expected keyboard
      pattern: roving tabindex (one tab stop per group), ArrowLeft / ArrowRight /
      ArrowUp / ArrowDown to move between choices, Home and End to jump to the ends.
      Disabled sizes are skipped, so the size group never traps focus on a choice
      that cannot be bought.
    - Selection changes and the add to cart validation message are announced to
      screen readers through polite and assertive live regions.
    - The size guide trigger reports its expanded state.
*/
(function () {
  function enabledOnly(btns) {
    return btns.filter(function (b) { return !b.disabled; });
  }

  // One tab stop per radio group, landing on the current choice when there is one.
  function updateRoving(btns, current) {
    var target = current && !current.disabled ? current : enabledOnly(btns)[0];
    btns.forEach(function (b) { b.tabIndex = b === target ? 0 : -1; });
  }

  function groupKeydown(e, btns, activate) {
    var list = enabledOnly(btns);
    if (!list.length) return;
    var idx = list.indexOf(document.activeElement);
    var next = null;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = list[(idx + 1 + list.length) % list.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = list[(idx - 1 + list.length) % list.length];
        break;
      case 'Home':
        next = list[0];
        break;
      case 'End':
        next = list[list.length - 1];
        break;
      default:
        return;
    }
    if (!next) return;
    e.preventDefault();
    next.focus();
    activate(next);
  }

  function initPdp(root) {
    if (root.dataset.kihInit) return;
    root.dataset.kihInit = '1';

    var dataEl = root.querySelector('[data-kih-variant-data]');
    if (!dataEl) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var colorIdx = data.colorPos > 0 ? data.colorPos - 1 : -1;
    var sizeIdx = data.sizePos > 0 ? data.sizePos - 1 : -1;
    var variants = data.variants || [];

    var idInput = root.querySelector('[data-kih-variant-id]');
    var atc = root.querySelector('[data-kih-atc]');
    var atcPrice = root.querySelector('[data-kih-atc-price]');
    var priceEl = root.querySelector('[data-kih-price]');
    var colorStatus = root.querySelector('[data-kih-color-status]');
    var sizeStatus = root.querySelector('[data-kih-size-status]');
    var sizeStep = root.querySelector('[data-kih-step="size"]');
    var validation = root.querySelector('[data-kih-validation]');
    var live = root.querySelector('[data-kih-live]');
    var colorBtns = Array.prototype.slice.call(root.querySelectorAll('[data-kih-color]'));
    var sizeBtns = Array.prototype.slice.call(root.querySelectorAll('[data-kih-size]'));

    var selectedColor = null;
    var selectedSize = null;
    var lastSpoken = '';
    // The URL is only rewritten after a real choice, so simply loading a product
    // page never rewrites the address bar out from under the shopper.
    var interacted = false;

    function announce(message) {
      if (!live || !message || message === lastSpoken) return;
      lastSpoken = message;
      live.textContent = message;
    }

    function variantFor(color, size) {
      return variants.find(function (v) {
        var okColor = colorIdx < 0 || v.options[colorIdx] === color;
        var okSize = sizeIdx < 0 || v.options[sizeIdx] === size;
        return okColor && okSize;
      });
    }

    function firstAvailableForColor(color) {
      return variants.find(function (v) {
        return (colorIdx < 0 || v.options[colorIdx] === color) && v.available;
      }) || variants.find(function (v) {
        return colorIdx < 0 || v.options[colorIdx] === color;
      });
    }

    /*
      Reflect the chosen variant in the address bar so the URL is shareable and so a
      refresh, a back/forward step or a bookmark lands on the same variant. Written
      with replaceState, not pushState: a colour change is not a navigation, and
      pushing one history entry per swatch would turn the browser Back button into a
      swatch-undo button.
    */
    function syncUrl(variantId) {
      if (!interacted || !variantId) return;
      if (!window.history || typeof window.history.replaceState !== 'function') return;
      try {
        var url = new URL(window.location.href);
        if (url.searchParams.get('variant') === String(variantId)) return;
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        // A blocked or unsupported history write must never break selection.
      }
    }

    /*
      Swap the gallery to the media Shopify has associated with this variant.

      Falls through silently in the two cases that are not errors: a product whose
      variants share one gallery (mediaId is 0, which the guard below catches), and a
      variant whose associated media is not among the slides rendered on the page. In
      both cases the gallery is simply left showing what it was showing, which is the
      correct image, rather than being blanked.
    */
    function switchMedia(mediaId) {
      if (!mediaId) return;
      var slides = root.querySelectorAll('.kih-pdp__slide');
      var found = false;
      slides.forEach(function (s) {
        var match = String(s.dataset.mediaId) === String(mediaId);
        s.classList.toggle('is-active', match);
        if (match) found = true;
      });
      if (found) {
        root.querySelectorAll('[data-kih-thumb]').forEach(function (t) {
          var on = String(t.dataset.kihThumb) === String(mediaId);
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-pressed', String(on));
        });
      }
    }

    function refreshSizeAvailability() {
      if (sizeIdx < 0) return;
      sizeBtns.forEach(function (btn) {
        var size = btn.dataset.kihSize;
        var v = variantFor(selectedColor, size);
        var enabled = !!selectedColor && !!v && v.available;
        btn.disabled = !enabled;
        btn.setAttribute('aria-disabled', String(!enabled));
        btn.classList.toggle('is-unavailable', !!selectedColor && (!v || !v.available));
      });
      updateRoving(sizeBtns, selectedSizeButton());
    }

    function selectedSizeButton() {
      return sizeBtns.filter(function (b) { return b.dataset.kihSize === selectedSize; })[0] || null;
    }

    function updateVariant() {
      var v = (selectedColor || colorIdx < 0) && (selectedSize || sizeIdx < 0)
        ? variantFor(selectedColor, selectedSize) : null;
      if (v && v.available) {
        if (idInput) idInput.value = v.id;
        if (atc) { atc.disabled = false; }
        if (atcPrice) atcPrice.textContent = v.price;
        if (priceEl) priceEl.textContent = v.price;
        switchMedia(v.mediaId);
        syncUrl(v.id);
        if (validation) { validation.hidden = true; validation.textContent = ''; }
        var parts = [];
        if (selectedColor) parts.push(selectedColor);
        if (selectedSize) parts.push('size ' + selectedSize);
        announce('Selected ' + parts.join(', ') + '. ' + v.price + '. Ready to add to cart.');
      } else {
        if (atc) atc.disabled = true;
      }
    }

    function selectColor(color) {
      selectedColor = color;
      var currentBtn = null;
      colorBtns.forEach(function (b) {
        var on = b.dataset.kihColor === color;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', String(on));
        if (on) currentBtn = b;
      });
      updateRoving(colorBtns, currentBtn);
      if (colorStatus) colorStatus.textContent = color;
      if (sizeStep) sizeStep.removeAttribute('data-locked');
      if (sizeStatus) sizeStatus.textContent = 'Choose a size';

      // Clear size only if invalid for the new color
      if (selectedSize) {
        var v = variantFor(selectedColor, selectedSize);
        if (!v || !v.available) { selectedSize = null; clearSizeSelection(); }
      }
      refreshSizeAvailability();

      var mv = firstAvailableForColor(color);
      if (mv && mv.mediaId) switchMedia(mv.mediaId);
      if (!selectedSize && sizeIdx >= 0) announce(color + ' selected. Sizes are now available to choose.');
      updateVariant();
    }

    function clearSizeSelection() {
      sizeBtns.forEach(function (b) { b.classList.remove('is-selected'); b.setAttribute('aria-checked', 'false'); });
    }

    function selectSize(size, btn) {
      if (btn.disabled) return;
      selectedSize = size;
      clearSizeSelection();
      btn.classList.add('is-selected');
      btn.setAttribute('aria-checked', 'true');
      updateRoving(sizeBtns, btn);
      if (sizeStatus) sizeStatus.textContent = size;
      updateVariant();
    }

    colorBtns.forEach(function (b) {
      b.addEventListener('click', function () { interacted = true; selectColor(b.dataset.kihColor); });
    });
    sizeBtns.forEach(function (b) {
      b.addEventListener('click', function () { interacted = true; selectSize(b.dataset.kihSize, b); });
    });

    var colorGroup = root.querySelector('[data-kih-step="color"] [role="radiogroup"]');
    if (colorGroup) {
      colorGroup.addEventListener('keydown', function (e) {
        groupKeydown(e, colorBtns, function (b) { interacted = true; selectColor(b.dataset.kihColor); });
      });
    }
    var sizeGroup = root.querySelector('[data-kih-step="size"] [role="radiogroup"]');
    if (sizeGroup) {
      sizeGroup.addEventListener('keydown', function (e) {
        groupKeydown(e, sizeBtns, function (b) { interacted = true; selectSize(b.dataset.kihSize, b); });
      });
    }
    updateRoving(colorBtns, null);
    updateRoving(sizeBtns, null);

    // Thumbnails
    root.querySelectorAll('[data-kih-thumb]').forEach(function (t) {
      t.addEventListener('click', function () { switchMedia(t.dataset.kihThumb); });
    });

    // Validation on submit
    var form = root.querySelector('#kih-product-form');
    if (form) {
      form.addEventListener('submit', function (e) {
        var incomplete = (colorIdx >= 0 && !selectedColor) || (sizeIdx >= 0 && !selectedSize);
        if (incomplete) {
          e.preventDefault();
          var message = !selectedColor && colorIdx >= 0 ? 'Please choose a color first.' : 'Please choose a size.';
          if (validation) {
            validation.hidden = false;
            validation.textContent = message;
          }
          var stepSel = !selectedColor && colorIdx >= 0 ? '[data-kih-step="color"]' : '[data-kih-step="size"]';
          var step = root.querySelector(stepSel);
          if (step) {
            var f = step.querySelector('button[tabindex="0"]') || step.querySelector('button:not([disabled])');
            if (f) f.focus();
          }
        } else {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'add_to_cart', ecommerce: { items: [{ variant_id: idInput ? idInput.value : null }] } });
        }
      });
    }

    // Auto-select single-value options (e.g. caps: one color)
    var uniqueColors = colorIdx < 0 ? [] : variants.reduce(function (acc, v) {
      if (acc.indexOf(v.options[colorIdx]) < 0) acc.push(v.options[colorIdx]); return acc;
    }, []);
    if (colorIdx >= 0 && uniqueColors.length === 1) selectColor(uniqueColors[0]);

    var uniqueSizes = sizeIdx < 0 ? [] : variants.reduce(function (acc, v) {
      if (acc.indexOf(v.options[sizeIdx]) < 0) acc.push(v.options[sizeIdx]); return acc;
    }, []);
    if (sizeIdx >= 0 && uniqueSizes.length === 1 && (colorIdx < 0 || uniqueColors.length === 1)) {
      var only = root.querySelector('[data-kih-size]');
      if (only) selectSize(uniqueSizes[0], only);
    }

    // The first announcement is the page load state, which should not be spoken.
    lastSpoken = live ? live.textContent : '';
    if (live) live.textContent = '';

    initSizeGuide(root);
  }

  function initSizeGuide(root) {
    var trigger = root.querySelector('[data-kih-size-guide]');
    var modal = document.querySelector('[data-kih-size-guide-modal]');
    if (!trigger || !modal) return;
    var dialog = modal.querySelector('.kih-modal__dialog');
    var lastFocus = null;
    var FOCUSABLE = 'a[href], button, input, select, textarea, [tabindex]';

    function focusables() {
      return Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return !el.disabled
          && el.getAttribute('tabindex') !== '-1'
          && el.getAttribute('aria-hidden') !== 'true'
          && (el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
      });
    }

    function open() {
      lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      trigger.setAttribute('aria-expanded', 'true');
      var closeBtn = modal.querySelector('.kih-modal__close');
      if (closeBtn) closeBtn.focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      modal.hidden = true;
      document.body.style.overflow = '';
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', onKey);
      if (lastFocus) lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'Tab') {
        var f = focusables();
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    trigger.setAttribute('aria-expanded', 'false');
    trigger.addEventListener('click', open);
    modal.querySelectorAll('[data-kih-modal-close]').forEach(function (b) { b.addEventListener('click', close); });
  }

  function boot() {
    document.querySelectorAll('[data-kih="pdp"]').forEach(initPdp);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-render
  document.addEventListener('shopify:section:load', boot);
})();
